import { useState, useCallback, useEffect, PropsWithChildren } from 'react'
import { ClientOnly, useAuthState } from '@based/sdk/react'
import { useSearchParams } from 'wouter'
import { AuthModal } from '@app/components/blocks/modal/AuthModal'
import {
  AuthTab,
  authTabs,
} from '@app/components/blocks/authentication/authTabs'
import {
  clearStoredPrefill,
  AUTH_QUERY_PARAM,
  OTC_CHALLENGE_QUERY_PARAM,
  OTC_QUERY_PARAM,
  UTM_SIGNUP_COMPLETED_QUERY_PARAM,
  clearStoredChallengeId,
} from '@app/components/blocks/authentication/authQueryParams'
import { AuthModalContext } from './AuthModalContext'

export const AuthModalProvider = ({ children }: PropsWithChildren) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tab, setTab] = useState<AuthTab>(authTabs.login)
  const [verificationTab, setVerificationTab] = useState<AuthTab | null>(null)
  const authState = useAuthState()
  const [searchParams, setSearchParams] = useSearchParams()

  const showAuthModal = useCallback((tab?: AuthTab) => {
    if (tab) {
      setTab(tab)
    }
    setIsModalOpen(true)
  }, [])

  useEffect(() => {
    const auth = searchParams.get(AUTH_QUERY_PARAM)
    const otc = searchParams.get(OTC_QUERY_PARAM)
    if (!auth && !otc) {
      return
    }
    if (authState?.userId && !isModalOpen) {
      clearStoredChallengeId('login')
      clearStoredChallengeId('signup')
      clearStoredPrefill('login')
      clearStoredPrefill('signup')
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(AUTH_QUERY_PARAM)
          next.delete(OTC_QUERY_PARAM)
          next.delete(OTC_CHALLENGE_QUERY_PARAM)
          return next
        },
        { replace: true },
      )
      return
    }
    if (authState?.userId) {
      return
    }
    if (otc === 'login') {
      setVerificationTab(authTabs.login)
      showAuthModal(authTabs.login)
    } else if (otc === 'signup') {
      setVerificationTab(authTabs.signUp)
      showAuthModal(authTabs.signUp)
    } else if (auth === 'login') {
      setVerificationTab(null)
      showAuthModal(authTabs.login)
    } else if (auth === 'signup') {
      setVerificationTab(null)
      showAuthModal(authTabs.signUp)
    }
  }, [
    searchParams,
    authState?.userId,
    isModalOpen,
    showAuthModal,
    setSearchParams,
  ])

  const closeModal = useCallback(
    (setUtm?: boolean) => {
      clearStoredChallengeId('login')
      clearStoredChallengeId('signup')
      clearStoredPrefill('login')
      clearStoredPrefill('signup')
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(AUTH_QUERY_PARAM)
          next.delete(OTC_QUERY_PARAM)
          next.delete(OTC_CHALLENGE_QUERY_PARAM)
          if (setUtm) {
            next.set(UTM_SIGNUP_COMPLETED_QUERY_PARAM, '1')
          }
          return next
        },
        { replace: true },
      )
      setIsModalOpen(false)
      setVerificationTab(null)
    },
    [setSearchParams],
  )

  const withAuthCheck = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
      if (authState && authState.userId) {
        return await action()
      } else {
        showAuthModal()
        return undefined
      }
    },
    [authState, showAuthModal],
  )

  const checkAuth = useCallback(() => {
    if (authState && authState.userId) {
      return true
    }
    showAuthModal()
    return false
  }, [authState, showAuthModal])

  const value = { withAuthCheck, checkAuth, showAuthModal }

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <ClientOnly>
        <AuthModal
          isOpen={isModalOpen}
          tab={tab}
          onClose={closeModal}
          verificationTab={verificationTab}
        />
      </ClientOnly>
    </AuthModalContext.Provider>
  )
}
